import {
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Select as ChakraSelect,
  Spinner,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, EditIcon, SmallCloseIcon } from '@chakra-ui/icons'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import Select, { SingleValue } from 'react-select'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { Header } from '../components/Header'
import { ConfirmDialog } from '../components/confirmDialog'
import { FormComponent } from '../components/formComponent'
import { customStyles } from '../components/dataTable'
import { formatVariables } from '../components/dataTable.support'
import { generateFormJSONForDataTables } from '../components/utils'
import { graphQLSendDataMappings, masterTypesInnerDataForTableRendering } from '../lib/formElementMappings'
import { formElementInterface, keyValue, keyValueObject } from '../lib/types'
import { SystemForm } from './systemForm'
import { SystemFormHeading } from './systemForm.support'
import {
  GET_ALL_TYPES,
  GET_PRODUCT_FILTER_OPTIONS,
  GET_PRODUCTS_BY_GENERIC_CODE,
  GET_SYSTEMS_BY_GENERIC_CODE,
  MUTATION_DELETE_PRODUCT,
  MUTATION_DELETE_SYSTEM,
  MUTATION_UPDATE_PRODUCT
} from '../queries/queries'

interface FilterOption {
  value: string
  label: string
}

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
}

interface ProductFilterOptionRow {
  generic_code: string
  product_category_id: string | null
  category_name: string | null
  product_sub_category_id: string | null
  product_sub_category_name: string | null
}

const showErrorMessage = (error: ApolloError): string => {
  if (error.clientErrors?.length) {
    return `ERR01: ${error.clientErrors[0].message}`
  }
  if (error.graphQLErrors?.length) {
    return `ERR02: ${error.graphQLErrors[0].message}`
  }
  if (error.networkError && !('result' in error.networkError)) {
    return `ERR03 :${error.networkError.message}`
  }
  if (error.networkError && 'result' in error.networkError) {
    return `ERR03 :${(error.networkError.result as { errors: { message: string }[] })?.errors?.[0].message}`
  }
  return error.message ?? 'Something went wrong'
}

const booleanBadgeCellStyles = (fieldKey: string): Array<{ when: (row: keyValue) => boolean; style: { backgroundColor: string; color: string } }> => [
  {
    when: (row: keyValue) => Boolean(row[fieldKey]),
    style: { backgroundColor: '#D4EDDA', color: '#155724' }
  },
  {
    when: (row: keyValue) => !Boolean(row[fieldKey]),
    style: { backgroundColor: '#E2E3E5', color: '#383D41' }
  }
]

export const ProductAdministration = (): JSX.Element => {
  const navigate = useNavigate()
  const toast = useToast()
  const [selectedGenericCode, setSelectedGenericCode] = useState<string | null>(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string | null>(null)
  const [isProductsSectionOpen, setIsProductsSectionOpen] = useState<boolean>(true)
  const [isSystemsSectionOpen, setIsSystemsSectionOpen] = useState<boolean>(true)

  const { data, loading, error } = useQuery(GET_PRODUCT_FILTER_OPTIONS) as {
    data: { getProductFilterOptions: ProductFilterOptionRow[] } | undefined
    loading: boolean
    error: unknown
  }

  const filterOptionRows = data?.getProductFilterOptions ?? []

  const categoryOptions: FilterOption[] = Array.from(
    filterOptionRows
      .filter(row => row.product_category_id && row.category_name)
      .reduce((map, row) => map.set(String(row.product_category_id), String(row.category_name)), new Map<string, string>())
      .entries()
  )
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const subCategoryOptions: FilterOption[] = Array.from(
    filterOptionRows
      .filter(row => row.product_sub_category_id && row.product_sub_category_name)
      .filter(row => !selectedCategoryId || String(row.product_category_id) === selectedCategoryId)
      .reduce((map, row) => map.set(String(row.product_sub_category_id), String(row.product_sub_category_name)), new Map<string, string>())
      .entries()
  )
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const options: FilterOption[] = Array.from(
    new Set(
      filterOptionRows
        .filter(row => !selectedCategoryId || String(row.product_category_id) === selectedCategoryId)
        .filter(row => !selectedSubCategoryId || String(row.product_sub_category_id) === selectedSubCategoryId)
        .map(row => row.generic_code)
    )
  )
    .sort()
    .map(code => ({ value: code, label: code }))

  const handleChange = (selected: SingleValue<FilterOption>): void => {
    setSelectedGenericCode(selected?.value ?? null)
  }

  const handleCategoryChange = (selected: SingleValue<FilterOption>): void => {
    setSelectedCategoryId(selected?.value ?? null)
    setSelectedSubCategoryId(null)
    setSelectedGenericCode(null)
  }

  const handleSubCategoryChange = (selected: SingleValue<FilterOption>): void => {
    setSelectedSubCategoryId(selected?.value ?? null)
    setSelectedGenericCode(null)
  }

  const handleResetFilters = (): void => {
    setSelectedCategoryId(null)
    setSelectedSubCategoryId(null)
    setSelectedGenericCode(null)
  }

  const { data: allTypes, loading: allTypesLoading } = useQuery(GET_ALL_TYPES, { errorPolicy: 'all' }) as {
    data: keyValueObject | undefined
    loading: boolean
  }

  const {
    data: productsData,
    loading: productsLoading
  }: { data: { productsByGenericCode: keyValue[] } | undefined; loading: boolean } = useQuery(GET_PRODUCTS_BY_GENERIC_CODE, {
    variables: { generic_code: selectedGenericCode },
    skip: !selectedGenericCode
  }) as { data: { productsByGenericCode: keyValue[] } | undefined; loading: boolean }

  const products = [...(productsData?.productsByGenericCode ?? [])].sort((a, b) => {
    const aActive = Boolean(a.is_active)
    const bActive = Boolean(b.is_active)
    if (aActive !== bActive) {
      return aActive ? -1 : 1
    }
    return String(a.product_code ?? '').localeCompare(String(b.product_code ?? ''))
  })

  const [formInputData, setFormInputData] = useState<formElementInterface[] | undefined>()
  const [formKey, setFormKey] = useState<number>(0)
  const [currentId, setCurrentId] = useState<string>('')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [IDToDelete, setIDToDelete] = useState<string | null>(null)

  const refetchQueries = selectedGenericCode ? [{ query: GET_PRODUCTS_BY_GENERIC_CODE, variables: { generic_code: selectedGenericCode } }] : []

  const [updateElement] = useMutation(MUTATION_UPDATE_PRODUCT, {
    variables: {},
    refetchQueries,
    onCompleted: () => {
      toast({ title: 'Success', description: 'Product saved', status: 'success', duration: 4000, isClosable: true })
    },
    onError: (mutationError: ApolloError) => {
      toast({ title: 'Error', description: showErrorMessage(mutationError), status: 'error', duration: 4000, isClosable: true })
    }
  })

  const [deleteElement] = useMutation(MUTATION_DELETE_PRODUCT, {
    variables: {},
    refetchQueries,
    onCompleted: () => {
      toast({ title: 'Success', description: 'Removed', status: 'success', duration: 4000, isClosable: true })
    },
    onError: (mutationError: ApolloError) => {
      toast({ title: 'Error', description: showErrorMessage(mutationError), status: 'error', duration: 4000, isClosable: true })
    }
  })

  const performEdit = (row: keyValue): void => {
    if (!allTypes) {
      return
    }
    setCurrentId((row.id as string) ?? '')
    setFormInputData(generateFormJSONForDataTables(row, allTypes, 'input'))
    setFormKey(k => k + 1)
    if (!isOpen) {
      onOpen()
    }
  }

  const performDelete = (row: keyValue): void => {
    setIDToDelete((row?.id as string) ?? null)
    setShowConfirm(true)
  }

  const formSubmit = (formData: keyValue): void => {
    const nestedFormObject = Object.keys(formData).reduce((reducer, item) => {
      return {
        ...reducer,
        [item]: graphQLSendDataMappings(item, formData[item] as string, 'input')
      }
    }, {})

    updateElement({
      variables: formatVariables('input', {
        ...nestedFormObject,
        id: currentId
      })
    })
      .then(() => {})
      .catch(() => {})
  }

  const handleConfirm = (): void => {
    setShowConfirm(false)
    if (IDToDelete) {
      deleteElement({ variables: { id: IDToDelete } })
        .then(() => {
          setIDToDelete(null)
        })
        .catch(() => {})
    }
  }

  const {
    data: systemsData,
    loading: systemsLoading,
    refetch: refetchSystems
  }: { data: { getSystemsByGenericCode: keyValue[] } | undefined; loading: boolean; refetch: () => void } = useQuery(GET_SYSTEMS_BY_GENERIC_CODE, {
    variables: { generic_code: selectedGenericCode },
    skip: !selectedGenericCode
  }) as { data: { getSystemsByGenericCode: keyValue[] } | undefined; loading: boolean; refetch: () => void }

  const getSystemProductCode = (row: keyValue): string => {
    const systemProducts = (row.system_products as unknown as keyValueObject[]) ?? []
    return ((systemProducts[0]?.product as keyValueObject)?.product_code as string) ?? ''
  }

  const getElementDisplay = (row: keyValue): string => {
    const systemElements = (row.system_elements as unknown as keyValueObject[]) ?? []
    const primary = systemElements.find(item => Boolean(item.is_primary)) ?? systemElements[0]
    const element = primary?.element as keyValueObject | undefined
    return (element?.element_name as string) ?? ''
  }

  const getSystemProductsDisplay = (row: keyValue): string => {
    const systemProducts = (row.system_products as unknown as keyValueObject[]) ?? []
    return systemProducts
      .map(item => (item.product as keyValueObject)?.product_code as string)
      .filter(Boolean)
      .join(', ')
  }

  const getServiceNames = (row: keyValue): string[] => {
    const systemServices = (row.system_services as unknown as keyValueObject[]) ?? []
    return systemServices
      .map(item => (item.service as keyValueObject)?.service_name as string | undefined)
      .filter((name): name is string => Boolean(name))
  }

  const getServicesDisplay = (row: keyValue): string => getServiceNames(row).join(', ')

  const getSystemReportsDisplay = (row: keyValue): string => {
    const systemReports = (row.system_reports as unknown as keyValueObject[]) ?? []
    return systemReports
      .map(item => (item.report as keyValueObject)?.report_no as string)
      .filter(Boolean)
      .join(', ')
  }

  const systems = [...(systemsData?.getSystemsByGenericCode ?? [])].sort((a, b) => {
    const productCodeCompare = getSystemProductCode(a).localeCompare(getSystemProductCode(b))
    if (productCodeCompare !== 0) {
      return productCodeCompare
    }
    const aActive = Boolean(a.is_active)
    const bActive = Boolean(b.is_active)
    if (aActive !== bActive) {
      return aActive ? -1 : 1
    }
    return 0
  })

  const [systemsFilterText, setSystemsFilterText] = useState<string>('')
  const [elementFilter, setElementFilter] = useState<string>('')
  const [serviceFilter, setServiceFilter] = useState<string>('')

  const elementFilterOptions = Array.from(
    new Set(
      systems
        .filter(row => !serviceFilter || getServiceNames(row).includes(serviceFilter))
        .map(row => getElementDisplay(row))
        .filter(Boolean)
    )
  ).sort()
  const serviceFilterOptions = Array.from(
    new Set(
      systems
        .filter(row => !elementFilter || getElementDisplay(row) === elementFilter)
        .flatMap(row => getServiceNames(row))
    )
  ).sort()

  const handleElementFilterChange = (value: string): void => {
    setElementFilter(value)
    setServiceFilter('')
  }

  const handleServiceFilterChange = (value: string): void => {
    setServiceFilter(value)
    setElementFilter('')
  }

  const filteredSystems = systems.filter(row => {
    const matchesText =
      !systemsFilterText ||
      Object.keys(row).some(key => String(JSON.stringify(row[key])).toLowerCase().includes(systemsFilterText.toLowerCase()))
    const matchesElement = !elementFilter || getElementDisplay(row) === elementFilter
    const matchesService = !serviceFilter || getServiceNames(row).includes(serviceFilter)
    return matchesText && matchesElement && matchesService
  })

  const { isOpen: isSystemOpen, onOpen: onSystemOpen, onClose: onSystemClose } = useDisclosure()
  const [systemFormData, setSystemFormData] = useState<keyValueObject>({})
  const [systemShowConfirm, setSystemShowConfirm] = useState<boolean>(false)
  const [systemIDToDelete, setSystemIDToDelete] = useState<string | null>(null)

  const systemsRefetchQueries = selectedGenericCode ? [{ query: GET_SYSTEMS_BY_GENERIC_CODE, variables: { generic_code: selectedGenericCode } }] : []

  const [deleteSystemElement] = useMutation(MUTATION_DELETE_SYSTEM, {
    variables: {},
    refetchQueries: systemsRefetchQueries,
    onCompleted: () => {
      toast({ title: 'Success', description: 'Removed', status: 'success', duration: 4000, isClosable: true })
    },
    onError: (mutationError: ApolloError) => {
      toast({ title: 'Error', description: showErrorMessage(mutationError), status: 'error', duration: 4000, isClosable: true })
    }
  })

  const performSystemEdit = (row: keyValue): void => {
    setSystemFormData(row as keyValueObject)
    onSystemOpen()
  }

  const performSystemDelete = (row: keyValue): void => {
    setSystemIDToDelete((row?.id as string) ?? null)
    setSystemShowConfirm(true)
  }

  const handleSystemConfirm = (): void => {
    setSystemShowConfirm(false)
    if (systemIDToDelete) {
      deleteSystemElement({ variables: { id: systemIDToDelete } })
        .then(() => {
          setSystemIDToDelete(null)
        })
        .catch(() => {})
    }
  }

  const systemColumns = [
    {
      name: '',
      cell: (row: keyValue) => (
        <>
          <Button onClick={() => { performSystemEdit(row) }}>
            <EditIcon w={3} />
          </Button>
          <Button onClick={() => { performSystemDelete(row) }} ml="3">
            <DeleteIcon w={3} />
          </Button>
        </>
      ),
      sortable: false,
      wrap: true,
      grow: 3,
      allowOverflow: true
    },
    {
      name: 'System Code',
      selector: (row: keyValue) => String(row.system_code ?? row.id ?? ''),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Product Code',
      selector: (row: keyValue) => getSystemProductCode(row),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Active',
      selector: (row: keyValue) => (Boolean(row.is_active) ? 'Yes' : 'No'),
      sortable: true,
      wrap: true,
      grow: 1,
      allowOverflow: true,
      center: true,
      conditionalCellStyles: booleanBadgeCellStyles('is_active')
    },
    {
      name: 'Display Record',
      selector: (row: keyValue) => (Boolean(row.display_record) ? 'Yes' : 'No'),
      sortable: true,
      wrap: true,
      grow: 1,
      allowOverflow: true,
      center: true,
      conditionalCellStyles: booleanBadgeCellStyles('display_record')
    },
    {
      name: 'FRL',
      selector: (row: keyValue) => String(row.frl_issued ?? ''),
      sortable: true,
      wrap: true,
      grow: 1,
      allowOverflow: true
    },
    {
      name: 'Fitting Allowed',
      selector: (row: keyValue) => String(row.fitting_allowed ?? ''),
      sortable: true,
      wrap: true,
      grow: 1,
      allowOverflow: true
    },
    {
      name: 'Element',
      selector: (row: keyValue) => getElementDisplay(row),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'System Products',
      selector: (row: keyValue) => getSystemProductsDisplay(row),
      sortable: false,
      wrap: true,
      grow: 3,
      allowOverflow: true
    },
    {
      name: 'Services',
      selector: (row: keyValue) => getServicesDisplay(row),
      sortable: false,
      wrap: true,
      grow: 3,
      allowOverflow: true
    },
    {
      name: 'System Reports',
      selector: (row: keyValue) => getSystemReportsDisplay(row),
      sortable: false,
      wrap: true,
      grow: 2,
      allowOverflow: true
    }
  ]

  const columns = [
    {
      name: '',
      cell: (row: keyValue) => (
        <>
          <Button onClick={() => { performEdit(row) }}>
            <EditIcon w={3} />
          </Button>
          <Button onClick={() => { performDelete(row) }} ml="3">
            <DeleteIcon w={3} />
          </Button>
        </>
      ),
      sortable: false,
      wrap: true,
      grow: 3,
      allowOverflow: true
    },
    {
      name: 'Product Code',
      selector: (row: keyValue) => String(row.product_code ?? ''),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Active',
      selector: (row: keyValue) => (Boolean(row.is_active) ? 'Yes' : 'No'),
      sortable: true,
      wrap: true,
      grow: 1,
      allowOverflow: true,
      center: true,
      conditionalCellStyles: booleanBadgeCellStyles('is_active')
    },
    {
      name: 'Product Abbreviation',
      selector: (row: keyValue) => String(row.product_abr ?? ''),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Product Description',
      selector: (row: keyValue) => String(row.product_desc ?? ''),
      sortable: true,
      wrap: true,
      grow: 3,
      allowOverflow: true
    },
    {
      name: 'Generic Code',
      selector: (row: keyValue) => String(row.generic_code ?? ''),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Product Category',
      selector: (row: keyValue) => masterTypesInnerDataForTableRendering('product_category', row.product_category as string | keyValue),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    },
    {
      name: 'Product Sub Category',
      selector: (row: keyValue) => masterTypesInnerDataForTableRendering('product_sub_category', row.product_sub_category as string | keyValue),
      sortable: true,
      wrap: true,
      grow: 2,
      allowOverflow: true
    }
  ]

  return (
    <Box>
      <Header title="Product Admin" />
      <Box p="8" maxW="960px">
        <Button
          variant="outline"
          colorScheme="gray"
          size="sm"
          mb="6"
          onClick={() => { navigate('/') }}
        >
          Back to Home
        </Button>
        <Flex gap="16px" flexWrap="wrap" align="flex-end">
          <FormControl w="240px">
            <FormLabel fontSize="sm" fontWeight="bold">
              Product Category
            </FormLabel>
            <Select<FilterOption>
              options={categoryOptions}
              isLoading={loading}
              isSearchable={true}
              isClearable={true}
              placeholder="Select a category..."
              value={categoryOptions.find(option => option.value === selectedCategoryId) ?? null}
              onChange={handleCategoryChange}
              menuPortalTarget={document.body}
              styles={selectMenuPortalStyles}
            />
          </FormControl>
          <FormControl w="240px">
            <FormLabel fontSize="sm" fontWeight="bold">
              Product Sub Category
            </FormLabel>
            <Select<FilterOption>
              options={subCategoryOptions}
              isLoading={loading}
              isSearchable={true}
              isClearable={true}
              placeholder="Select a sub category..."
              value={subCategoryOptions.find(option => option.value === selectedSubCategoryId) ?? null}
              onChange={handleSubCategoryChange}
              menuPortalTarget={document.body}
              styles={selectMenuPortalStyles}
            />
          </FormControl>
          <FormControl w="240px">
            <FormLabel fontSize="sm" fontWeight="bold">
              Select Product
            </FormLabel>
            <Select<FilterOption>
              options={options}
              isLoading={loading}
              isSearchable={true}
              isClearable={true}
              placeholder="Select a product..."
              value={options.find(option => option.value === selectedGenericCode) ?? null}
              onChange={handleChange}
              menuPortalTarget={document.body}
              styles={selectMenuPortalStyles}
            />
          </FormControl>
          <Button
            variant="outline"
            colorScheme="gray"
            size="sm"
            h="38px"
            onClick={handleResetFilters}
            isDisabled={!selectedCategoryId && !selectedSubCategoryId && !selectedGenericCode}
          >
            Reset Filters
          </Button>
        </Flex>
        {Boolean(error) && (
          <Box mt="4" color="red.500" fontSize="sm">
            Failed to load products.
          </Box>
        )}
      </Box>

      {selectedGenericCode && (
        <Box px="8" pb="8">
          <Flex align="center" justify="space-between">
            <Box flex="1">
              <SystemFormHeading title="Related Products" />
            </Box>
            <IconButton
              aria-label={isProductsSectionOpen ? 'Collapse Related Products' : 'Expand Related Products'}
              title={isProductsSectionOpen ? 'Collapse' : 'Expand'}
              size="sm"
              variant="ghost"
              onClick={() => { setIsProductsSectionOpen(prev => !prev) }}
            >
              {isProductsSectionOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </Flex>
          <Collapse in={isProductsSectionOpen} animateOpacity>
            {(productsLoading || allTypesLoading) && (
              <Flex m="8" alignItems="center" justifyContent="center" flexDirection="column">
                <Spinner size="xl" color="primary" thickness="4px" />
                <Box mt="4" fontSize="lg" color="gray.600">
                  Loading products...
                </Box>
              </Flex>
            )}
            {!productsLoading && !allTypesLoading && products.length === 0 && (
              <Heading size="sm" color="gray.600">
                No products found for this product code.
              </Heading>
            )}
            {!productsLoading && !allTypesLoading && products.length > 0 && (
              <Box overflow={'auto'}>
                <DataTable
                  columns={columns}
                  data={products}
                  pagination
                  customStyles={customStyles}
                  keyField="id"
                  fixedHeader
                  responsive
                  highlightOnHover
                  persistTableHead
                  striped
                  paginationPerPage={10}
                  paginationComponentOptions={{ selectAllRowsItem: true }}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      )}

      {selectedGenericCode && (
        <Box px="8" pb="8">
          <Flex align="center" justify="space-between">
            <Box flex="1">
              <SystemFormHeading title="Related Systems" />
            </Box>
            <IconButton
              aria-label={isSystemsSectionOpen ? 'Collapse Related Systems' : 'Expand Related Systems'}
              title={isSystemsSectionOpen ? 'Collapse' : 'Expand'}
              size="sm"
              variant="ghost"
              onClick={() => { setIsSystemsSectionOpen(prev => !prev) }}
            >
              {isSystemsSectionOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </Flex>
          <Collapse in={isSystemsSectionOpen} animateOpacity>
            {systemsLoading && (
              <Flex m="8" alignItems="center" justifyContent="center" flexDirection="column">
                <Spinner size="xl" color="primary" thickness="4px" />
                <Box mt="4" fontSize="lg" color="gray.600">
                  Loading systems...
                </Box>
              </Flex>
            )}
            {!systemsLoading && systems.length === 0 && (
              <Heading size="sm" color="gray.600">
                No systems found for this product code.
              </Heading>
            )}
            {!systemsLoading && systems.length > 0 && (
              <>
                <Flex gap="8px" mb="4" flexWrap="wrap" alignItems="center">
                  <Flex gap="4px" maxW="300px">
                    <Input
                      type="text"
                      fontSize="12px"
                      placeholder="Search systems..."
                      value={systemsFilterText}
                      onChange={e => { setSystemsFilterText(e.target.value) }}
                    />
                    <IconButton
                      size="md"
                      onClick={() => { setSystemsFilterText('') }}
                      color="current"
                      aria-label="Clear Filter"
                      title="Clear Filter"
                    >
                      <SmallCloseIcon />
                    </IconButton>
                  </Flex>
                  <ChakraSelect
                    maxW="220px"
                    h="34px"
                    fontSize="12px"
                    placeholder="All Elements"
                    value={elementFilter}
                    onChange={e => { handleElementFilterChange(e.target.value) }}
                  >
                    {elementFilterOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </ChakraSelect>
                  <ChakraSelect
                    maxW="220px"
                    h="34px"
                    fontSize="12px"
                    placeholder="All Services"
                    value={serviceFilter}
                    onChange={e => { handleServiceFilterChange(e.target.value) }}
                  >
                    {serviceFilterOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </ChakraSelect>
                </Flex>
                {filteredSystems.length === 0 && (
                  <Heading size="sm" color="gray.600">
                    No systems match your filters.
                  </Heading>
                )}
                {filteredSystems.length > 0 && (
                  <Box overflow={'auto'}>
                    <DataTable
                      columns={systemColumns}
                      data={filteredSystems}
                      pagination
                      customStyles={customStyles}
                      keyField="id"
                      fixedHeader
                      responsive
                      highlightOnHover
                      persistTableHead
                      striped
                      paginationPerPage={10}
                      paginationComponentOptions={{ selectAllRowsItem: true }}
                    />
                  </Box>
                )}
              </>
            )}
          </Collapse>
        </Box>
      )}

      {showConfirm && (
        <ConfirmDialog
          callBack={handleConfirm}
          cancelCallBack={() => {
            setShowConfirm(false)
          }}
        />
      )}

      {systemShowConfirm && (
        <ConfirmDialog
          callBack={handleSystemConfirm}
          cancelCallBack={() => {
            setSystemShowConfirm(false)
          }}
        />
      )}

      <Drawer isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize={'23px'} color={'purple.700'} fontWeight="black" shadow="md" border="1">
            Update Form
          </DrawerHeader>
          <DrawerBody color={'000'} p="5">
            {formInputData && (
              <FormComponent
                key={formKey}
                cssProps={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'stretch',
                  bg: 'red.100'
                }}
                onSubmitCallBack={(formData: keyValue) => {
                  formSubmit(formData)
                }}
                inputData={formInputData}
                renderForm={1}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Drawer isOpen={isSystemOpen} onClose={onSystemClose} size="md" closeOnOverlayClick={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize={'23px'} color={'purple.700'} fontWeight="black" shadow="md" border="1">
            Update Form
          </DrawerHeader>
          <DrawerBody color={'000'} p="5">
            {allTypes && (
              <SystemForm
                mode="edit"
                formData={systemFormData}
                allTypes={allTypes}
                callBack={() => {
                  refetchSystems()
                  setTimeout(() => {
                    onSystemClose()
                  }, 1000)
                }}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}
